/* @jsx h */
import { defineComponent, h, computed, ref } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import {
  CategoryProductTree,
  type CategoryTreeColumn,
  type CategoryTreeGroup,
} from "@app/vue/components/CategoryProductTree";

type ColumnSpec = {
  key: string;
  labelKey: string;
  total?: boolean;
  format:
    | "text"
    | "money"
    | "integer"
    | "quantity"
    | "decimal"
    | "percentage"
    | "date"
    | "timestamp";
};

type CategoryRow = Record<string, unknown> & {
  category_id: number;
  category: string;
  product_id: number | null;
  product: string;
};

const HIDDEN_INTERACTIVE_COLUMNS = new Set(["net_sales"]);

/**
 * Text columns other than category/product (e.g. the tax report's rate) refine
 * the leaf, so they're appended to the product label: "Coffee · VAT (8.1%)".
 */
const TREE_GROUP_COLUMNS = new Set(["category", "product"]);

/**
 * Category report's detail table, grouped into a collapsible category →
 * product tree (matching Chart of Accounts' `ChartCategoriesTab`) instead of
 * the flat row-per-product table `ReportTable` renders for every other
 * report. The rows arriving here are still the flat, keyset-paginated shape
 * `dataroom_category_rows` returns — grouping happens client-side over
 * whatever pages have loaded so far.
 */
export const CategoryReportTable = defineComponent({
  name: "DataRoomCategoryReportTable",
  props: {
    spec: { type: Object, required: true },
    rows: { type: Array, default: () => [] },
    currency: { type: String, default: "CHF" },
    loading: { type: Boolean, default: false },
    loadingMore: { type: Boolean, default: false },
    hasMore: { type: Boolean, default: false },
    onLoadMore: { type: Function, default: undefined },
  },
  setup(props: any) {
    const { t } = useLocalization();
    const collapsed = ref(new Set<number>());

    const labelColumns = computed(() =>
      ((props.spec?.columns ?? []) as ColumnSpec[])
        .filter((c) => c.format === "text" && !TREE_GROUP_COLUMNS.has(c.key))
        .map((c) => c.key),
    );

    const columns = computed<CategoryTreeColumn[]>(() =>
      ((props.spec?.columns ?? []) as ColumnSpec[])
        .filter(
          (column) =>
            column.format !== "text" &&
            !HIDDEN_INTERACTIVE_COLUMNS.has(column.key),
        )
        .map((c) => ({
          key: c.key,
          format:
            c.format === "integer" || c.format === "quantity"
              ? c.format
              : "money",
          label: t(c.labelKey),
          // Only additive columns roll up into the category subtotal — the
          // current unit price doesn't.
          total: c.total === true,
        })),
    );

    const categories = computed<CategoryTreeGroup[]>(() => {
      const rows = props.rows as CategoryRow[];
      const order: number[] = [];
      const groups = new Map<number, CategoryTreeGroup>();
      for (const row of rows) {
        let group = groups.get(row.category_id);
        if (!group) {
          group = {
            id: row.category_id,
            name: row.category,
            values: {},
            products: [],
          };
          groups.set(row.category_id, group);
          order.push(row.category_id);
        }
        for (const col of columns.value) {
          if (!col.total) continue;
          group.values[col.key] =
            (group.values[col.key] ?? 0) + (Number(row[col.key]) || 0);
        }
        if (row.product_id != null) {
          const values: Record<string, number | null> = {};
          for (const col of columns.value)
            values[col.key] =
              row[col.key] == null ? null : Number(row[col.key]) || 0;
          group.products.push({
            // One product can appear on several rows (one per tax rate).
            id: String(row[props.spec?.rowKey] ?? row.product_id),
            name: [row.product, ...labelColumns.value.map((k) => row[k])]
              .filter((part) => part != null && part !== "")
              .join(" · "),
            values,
          });
        }
      }
      return order.map((id) => groups.get(id)!);
    });

    const toggleCategory = (categoryId: number | string) => {
      const next = new Set(collapsed.value);
      next.has(categoryId as number)
        ? next.delete(categoryId as number)
        : next.add(categoryId as number);
      collapsed.value = next;
    };

    return () => (
      <div class="flex flex-col gap-3">
        <CategoryProductTree
          categories={categories.value}
          columns={columns.value}
          currency={props.currency}
          collapsed={collapsed.value}
          onToggleCategory={toggleCategory}
          loading={props.loading}
          emptyMessage={t("reports.dataroom.table_empty")}
          productColumnLabel={t("reports.dataroom.col.product")}
          categoryTotalLabel={t("reports.dataroom.group_total")}
          expandLabel={(name: string) =>
            t("reports.coa.expand_category", [name])
          }
          collapseLabel={(name: string) =>
            t("reports.coa.collapse_category", [name])
          }
        />
        {props.spec?.grainKey && (
          <div class="rounded-2xl bg-base-200/40 px-4 py-2 text-[11px] text-base-content/65">
            {t(props.spec.grainKey)}
          </div>
        )}
        {props.hasMore && (
          <div class="flex justify-center rounded-2xl bg-base-200/40 p-3">
            <button
              type="button"
              class="btn btn-sm bg-base-100 text-base-content hover:border-primary hover:bg-primary/10 hover:text-primary"
              disabled={props.loadingMore}
              onClick={() => props.onLoadMore?.()}
            >
              {props.loadingMore ? (
                <span class="loading loading-spinner loading-xs" />
              ) : (
                <span class="material-symbols-outlined text-base">
                  expand_more
                </span>
              )}
              {t("reports.dataroom.table_load_more")}
            </button>
          </div>
        )}
      </div>
    );
  },
});
